import {
    Button,
    Card,
    DialogBody,
    Icon,
    Intent,
    OverlayToaster,
    Position,
    Spinner,
    Tag
} from "@blueprintjs/core";
import { segmentTrackRestudyKeepBaseAssumptionsClicked } from "analytics/analyticTrackEvents";
import {
    getQueueProjectsExportColumnsForRegion,
    QueueProjectsExportColumn
} from "components/sidebar/queueProjects/queueProjectsExportColumn";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    useGetQueueProjectHopsQuery,
    useGetRelatedQueueProjectsQuery
} from "generated/graphql";
import { parse, ParseResult } from "papaparse";
import React, { useMemo } from "react";
import { GeneratorType } from "types/generatorType";
import {
    getScoreBucket,
    SCORE_BUCKET_LABEL,
    SCORE_BUCKET_SORT_ORDER,
    toQueueProjectRelatedness
} from "types/queueProjectRelatednessType";
import { QueueProject } from "types/queueProjectsType";
import { RegionType } from "types/regionType";
import { MISO_2022_REALISTIC_SCOPE_TYPE } from "types/scope";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "types/scopeViewType";
import { downloadJSONToCSV } from "utils/csv";
import {
    computeQueueProjectStats,
    QueueProjectsByFuelType,
    QueueProjectStats,
    queueProjectsToExportObject,
    queueProjectTogglesEqual,
    validateAndCreateEditedProjects
} from "utils/queueProjectUtils";
import "./QueuedGenPanel.scss";

/**
 * Bucketed impact tier formatted for CSV export. The bucket label is
 * prefixed with its ordinal (`1:` … `5:`) so Excel's default
 * lexicographic sort orders the column meaningfully — ascending puts
 * Dominant first, descending puts Negligible first. Projects with no
 * relatedness data (not returned by the query / no constraint
 * contribution) are folded into the Negligible bucket since "no
 * measurable impact" is functionally indistinguishable from a tiny
 * non-zero impact.
 */
const buildRelatednessScoreColumn = (
    niraBusId: string,
    scoreByProjectNumber: ReadonlyMap<string, number>
): QueueProjectsExportColumn => ({
    getHeader: () => `Impact on ${niraBusId} (at 500 MW project size)`,
    renderExportValue: (queueProject) => {
        const score = scoreByProjectNumber.get(queueProject.projectNumber) ?? 0;
        const bucket = getScoreBucket(score);
        return `${SCORE_BUCKET_SORT_ORDER[bucket]}: ${SCORE_BUCKET_LABEL[bucket]}`;
    },
    parseExportValue: () => ({})
});

/**
 * Number of network hops between the study bus and a queue project. Projects
 * with no hops entry (outside the studied hop radius, or hops data not
 * available) are left blank rather than coerced to a number, since "unknown
 * distance" is not the same as "zero hops away". Display-only, so the upload
 * parser ignores it.
 */
const buildHopsColumn = (
    niraBusId: string,
    hopsByProjectNumber: ReadonlyMap<string, number>
): QueueProjectsExportColumn => ({
    getHeader: () => `Bus Hops to ${niraBusId}`,
    renderExportValue: (queueProject) => {
        const numHops = hopsByProjectNumber.get(queueProject.projectNumber);
        return numHops === undefined ? "" : `${numHops}`;
    },
    parseExportValue: () => ({})
});

const QueuedGenPanelToaster = OverlayToaster.create({
    position: Position.TOP
});

interface QueuedGenPanelProps {
    readonly queueProjectsByFuelType: QueueProjectsByFuelType;
    readonly baseScopeLabel: string;
    readonly editedQueueProjects: ReadonlyArray<QueueProject> | undefined;
    readonly onEditedQueueProjectsChanged: (
        projects: ReadonlyArray<QueueProject>
    ) => void;
}

const QueuedGenPanel: React.FC<QueuedGenPanelProps> = ({
    queueProjectsByFuelType,
    baseScopeLabel,
    editedQueueProjects,
    onEditedQueueProjectsChanged
}) => {
    const { bus } = useBusPageConfiguration();
    const baseQueueProjects =
        Array.from(queueProjectsByFuelType.values())[0] ?? [];

    const isMisoBus = bus.region === RegionType.MISO;
    const firstFuelType: GeneratorType | undefined = Array.from(
        queueProjectsByFuelType.keys()
    )[0];
    const sortScopeView =
        firstFuelType === undefined
            ? undefined
            : getCapacityCostsScopeViewFromRegionAndGenerator(
                  bus.region,
                  firstFuelType
              );

    // Pin scope to miso_2022_realistic so the relatedness score is a stable
    // POI-level metric across base-scope choices. scope_view still tracks
    // the user's first selected fuel type so the underlying constraint set
    // matches the generator type they're scoping for.
    const relatednessQuery = useGetRelatedQueueProjectsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope: MISO_2022_REALISTIC_SCOPE_TYPE,
            scope_view: sortScopeView ?? "",
            region: bus.region
        },
        skip: !isMisoBus || sortScopeView === undefined
    });

    // Network-proximity (hops) for the study bus, keyed on the
    // scope_view/region grain. There is no `scope` column on the hops table.
    // Runs for every region: on MISO it supplements the impact score, and on
    // regions without relatedness data (e.g. WECC) it's the sole sort metric
    // and column in the exported CSV.
    const hopsQuery = useGetQueueProjectHopsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope_view: sortScopeView ?? "",
            region: bus.region
        },
        skip: sortScopeView === undefined
    });

    const { scoreByProjectNumber, hopsByProjectNumber } = useMemo(
        () =>
            toQueueProjectRelatedness(
                isMisoBus ? relatednessQuery.data : undefined,
                // Treat a query error (e.g. the table isn't deployed here) as
                // "no hops data" so the CSV still exports without the hops
                // column.
                hopsQuery.error !== undefined ? undefined : hopsQuery.data
            ),
        [isMisoBus, relatednessQuery.data, hopsQuery.data, hopsQuery.error]
    );

    const isExportDataLoading =
        (isMisoBus && relatednessQuery.loading) || hopsQuery.loading;

    return (
        <DialogBody className="QueuedGenPanel-body">
            <QueuedGenCards
                region={bus.region}
                niraBusId={bus.niraBusId}
                baseScopeLabel={baseScopeLabel}
                baseProjects={baseQueueProjects}
                editedProjects={editedQueueProjects}
                onEditedQueueProjectsChanged={onEditedQueueProjectsChanged}
                scoreByProjectNumber={scoreByProjectNumber}
                hopsByProjectNumber={hopsByProjectNumber}
                isExportDataLoading={isExportDataLoading}
            />
        </DialogBody>
    );
};

const StatsTags: React.FC<{
    readonly stats?: QueueProjectStats;
}> = ({ stats }) => (
    <div
        className="QueuedGenPanel-stats"
        style={{ visibility: stats !== undefined ? "visible" : "hidden" }}
    >
        {stats !== undefined && (
            <>
                <Tag minimal round intent="primary">
                    {stats.includedCount}/{stats.totalCount} projects
                </Tag>
                <Tag minimal round intent="primary">
                    {stats.includedCapacityGw.toFixed(1)}/
                    {stats.totalCapacityGw.toFixed(1)} GW
                </Tag>
            </>
        )}
    </div>
);

const QueuedGenCards: React.FC<{
    readonly region: RegionType;
    readonly niraBusId: string;
    readonly baseScopeLabel: string;
    readonly baseProjects: ReadonlyArray<QueueProject>;
    readonly editedProjects: ReadonlyArray<QueueProject> | undefined;
    readonly onEditedQueueProjectsChanged: (
        projects: ReadonlyArray<QueueProject>
    ) => void;
    readonly scoreByProjectNumber: ReadonlyMap<string, number>;
    readonly hopsByProjectNumber: ReadonlyMap<string, number>;
    readonly isExportDataLoading: boolean;
}> = ({
    region,
    niraBusId,
    baseScopeLabel,
    baseProjects,
    editedProjects,
    onEditedQueueProjectsChanged,
    scoreByProjectNumber,
    hopsByProjectNumber,
    isExportDataLoading
}) => {
    const baseExportColumns = getQueueProjectsExportColumnsForRegion(region);
    const isMisoBus = region === RegionType.MISO;
    // Only append the Hops column when hops data is actually present, so the
    // CSV doesn't carry an all-blank column in environments where the hops
    // table isn't available. Impact (DFAX relatedness) is MISO-only; hops are
    // exported for any region that has them (e.g. WECC).
    const hasHops = hopsByProjectNumber.size > 0;
    const exportColumns: ReadonlyArray<QueueProjectsExportColumn> = [
        ...baseExportColumns,
        ...(isMisoBus
            ? [buildRelatednessScoreColumn(niraBusId, scoreByProjectNumber)]
            : []),
        ...(hasHops ? [buildHopsColumn(niraBusId, hopsByProjectNumber)] : [])
    ];

    // Hops ascending (electrically closest first). Projects with no hops entry
    // sink to the bottom. Project number is the final tiebreak for stability.
    const compareByHops = (a: QueueProject, b: QueueProject): number => {
        const hopsA = hopsByProjectNumber.get(a.projectNumber) ?? Infinity;
        const hopsB = hopsByProjectNumber.get(b.projectNumber) ?? Infinity;
        if (hopsA !== hopsB) {
            return hopsA - hopsB;
        }
        return a.projectNumber.localeCompare(b.projectNumber);
    };

    // Sort by contribution score descending so the projects most entangled
    // with this study bus float to the top, then by hops ascending (closest
    // first) to break ties, mirroring the on-screen related-projects table.
    // Project number is the final tiebreak for stability.
    const compareByRelatedness = (a: QueueProject, b: QueueProject): number => {
        const scoreA = scoreByProjectNumber.get(a.projectNumber) ?? 0;
        const scoreB = scoreByProjectNumber.get(b.projectNumber) ?? 0;
        const scoreDiff = scoreB - scoreA;
        if (scoreDiff !== 0) {
            return scoreDiff;
        }
        return compareByHops(a, b);
    };

    // MISO ranks by impact (hops breaks ties); other regions with hops data
    // (e.g. WECC) rank purely by hops, low to high. Without hops there's no
    // meaningful ordering, so leave the projects in their source order.
    const compareFn = isMisoBus
        ? compareByRelatedness
        : hasHops
        ? compareByHops
        : undefined;

    const baseStats = computeQueueProjectStats(baseProjects);
    const editedStats = editedProjects
        ? computeQueueProjectStats(editedProjects)
        : undefined;

    const today = new Date().toISOString().split("T")[0];

    const handleDownloadBase = async () => {
        const exportObjects = queueProjectsToExportObject(
            baseProjects,
            exportColumns,
            compareFn
        );
        await downloadJSONToCSV({
            json: exportObjects,
            filename: `Nira Queue Projects - ${niraBusId} - ${baseScopeLabel} - ${today}.csv`
        });
    };

    const showUploadError = (message: string) => {
        const retainedAssumptions =
            editedProjects === undefined
                ? "No assumptions have been selected."
                : queueProjectTogglesEqual(editedProjects, baseProjects)
                ? "Base assumptions are still selected."
                : "Your previously uploaded assumptions are still selected.";
        QueuedGenPanelToaster.show({
            intent: Intent.DANGER,
            message: `CSV not applied. ${message} ${retainedAssumptions}`
        });
    };

    const handleUpload = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".csv, text/csv";
        input.onchange = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target.files == null) {
                return;
            }
            const file = target.files[0];
            parse<Record<string, string>>(file, {
                header: true,
                skipEmptyLines: true,
                error: () => {
                    showUploadError("Failed to read file.");
                },
                complete: (results: ParseResult<Record<string, string>>) => {
                    if (results.errors.length > 0) {
                        showUploadError("Failed to parse CSV.");
                        return;
                    }
                    const validation = validateAndCreateEditedProjects(
                        results.data,
                        baseProjects,
                        exportColumns
                    );
                    if (validation.type === "error") {
                        showUploadError(validation.message);
                        return;
                    }
                    onEditedQueueProjectsChanged(validation.projects);
                }
            });
        };
        input.click();
    };

    return (
        <div className="QueuedGenPanel-content">
            <Card className="QueuedGenPanel-card">
                <div className="QueuedGenPanel-card-left">
                    <h6 className="bp5-heading QueuedGenPanel-card-title">
                        1. Download base assumptions
                    </h6>
                    <p className="QueuedGenPanel-description">
                        Download the queue projects from the base scope. Each
                        project has an &quot;Included&quot; column indicating
                        whether it&apos;s included in the base scope.
                    </p>
                    {isMisoBus && (
                        <p className="QueuedGenPanel-description">
                            Impact is calculated assuming an energy and capacity
                            size of 500 MW.
                        </p>
                    )}
                </div>
                <div className="QueuedGenPanel-card-right">
                    <StatsTags stats={baseStats} />
                    <div className="QueuedGenPanel-card-buttons">
                        {region === RegionType.WECC && (
                            <Button
                                fill
                                text="Keep base assumptions"
                                onClick={() => {
                                    segmentTrackRestudyKeepBaseAssumptionsClicked(
                                        niraBusId,
                                        region
                                    );
                                    onEditedQueueProjectsChanged(baseProjects);
                                }}
                            />
                        )}
                        <Button
                            fill
                            intent={Intent.PRIMARY}
                            icon={
                                isExportDataLoading ? (
                                    <Spinner size={16} />
                                ) : (
                                    "download"
                                )
                            }
                            text="Download CSV"
                            disabled={isExportDataLoading}
                            onClick={handleDownloadBase}
                        />
                    </div>
                </div>
            </Card>
            <Icon
                icon="arrow-down"
                className="QueuedGenPanel-arrow"
                size={20}
            />
            <Card className="QueuedGenPanel-card">
                <div className="QueuedGenPanel-card-left">
                    <h6 className="bp5-heading QueuedGenPanel-card-title">
                        2. Upload custom assumptions
                    </h6>
                    <p className="QueuedGenPanel-description">
                        Edit the &quot;Included&quot; column in the downloaded
                        CSV to reflect your custom assumptions. Do not add,
                        remove, or edit any other fields. Upload the edited CSV
                        to apply your changes.
                    </p>
                </div>
                <div className="QueuedGenPanel-card-right">
                    <StatsTags stats={editedStats} />
                    <div className="QueuedGenPanel-card-buttons">
                        <Button
                            fill
                            intent={Intent.PRIMARY}
                            icon="upload"
                            text="Upload assumptions"
                            onClick={handleUpload}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default QueuedGenPanel;
